import type { SetupContext } from 'vue'
import type { RegularFilter, RegularFilterConfig } from '../../models'
import type { DomainServiceProvider } from '../../providers/interfaces'
import type { IToolingBaseComposableDataItem } from '../use-tooling-base/types'
import type {
  IToolingFormComposableComputed,
  IToolingFormComposableComputedParams,
  IToolingFormComposableProps,
} from './types'
import { computed } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FiltersUtils } from '../../utils/filters.utils'
import { useInstance } from '../use-instance'
import { computedFn as toolingBaseComputedFn } from '../use-tooling-base/computed'
import { useToolingPivots } from '../use-tooling-pivots'
import { getFiltersFromValues } from './utils'

export const computedFn = <DataItemType extends IToolingBaseComposableDataItem>(
  props: IToolingFormComposableProps,
  ctx: SetupContext,
  params: IToolingFormComposableComputedParams<DataItemType>,
): IToolingFormComposableComputed<DataItemType> => {
  const toolingBaseComputed = toolingBaseComputedFn<DataItemType>()
  const { filtersConfig } = toolingBaseComputed

  const { instanceConfig } = useInstance()

  const { pivots, visiblePivots } = useToolingPivots({
    pivotable: params.pivotable,
    extraConfig: params.pivotsExtraConfig,
  })

  const itemPivots = computed<Record<string, RegularFilter>>(() =>
    pivots.value.reduce<Record<string, RegularFilter>>(
      (result, pivot) => ({
        ...result,
        [pivot.config.id]: pivot,
      }),
      {},
    ),
  )

  const visibleItemPivots = computed<Record<string, RegularFilter>>(() =>
    visiblePivots.value.reduce<Record<string, RegularFilter>>(
      (result, pivot) => ({
        ...result,
        [pivot.config.id]: pivot,
      }),
      {},
    ),
  )

  const previewFilters = computed((): Record<string, RegularFilter> => {
    const previewFiltersConfig = Object.values(filtersConfig.value).reduce(
      (result: Record<string, RegularFilterConfig>, config) => {
        const itemPivot = params.item.value.criteria.pivots?.find(
          ({ name }) => name === FiltersUtils.getEncodedId(config),
        )
        result[config.id] = {
          ...config,
          values: config.values.filter(valueConfig => {
            if (!itemPivot?.values.length) {
              return true
            }
            return itemPivot.values.includes(FiltersUtils.encodeValue(config, valueConfig.id))
          }),
          mandatory: !config.notMandatoryInExplain,
          multiselectDisabled: !instanceConfig.value.allowMultipleValuesInSearchFilters,
        }
        return result
      },
      {},
    )
    const filtersFromValues = getFiltersFromValues(
      previewFiltersConfig,
      params.previewFiltersSelectedValues.value,
    )
    return filtersFromValues
  })

  const isValid = computed(() => {
    const domainService = CONTAINER.get<DomainServiceProvider<DataItemType>>(
      TYPES.DomainServiceProvider,
    )(toolingBaseComputed.featureId.value)
    return domainService.isValid(params.item.value)
  })

  return {
    ...toolingBaseComputed,
    itemPivots,
    visibleItemPivots,
    previewFilters,
    isValid,
  }
}
